/** Per-session media gallery.
 *
 * Renders every Evidence row for one session — snapshots, whiteboards,
 * clips, recordings, chat attachments — as a card grid. Plus a top-of-
 * page "Download inspection report (PDF)" affordance that calls the
 * existing `/v1/sessions/{id}/inspection-report.pdf` endpoint (the
 * redesigned beautiful PDF we shipped earlier).
 *
 * Cards:
 *   - Snapshots / whiteboards → thumbnail (presigned S3 URL) with a
 *     click-through to the full-size download.
 *   - Clips / recordings → native <video> with `preload="metadata"`
 *     so we get a poster + duration without blowing the network.
 *   - Documents → file-type icon + filename + download.
 *
 * The endpoint is the same `/v1/sessions/{id}/evidence/` we've had
 * since Phase 1B; the new `?with_urls=true` query param presigns one
 * S3 GET per row in the response.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import { StatCard } from "@/components/StatCard";
import { ApiPaths, api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { Evidence, EvidenceKind, Session } from "@/lib/types";
import { ChatPanel } from "@/modules/chat";
import { EvidenceViewerModal } from "@/modules/evidence/EvidenceViewerModal";
import { InspectionQaPanel, InspectionReportPanel } from "@/modules/sessions";


export default function SessionMediaPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  return (
    <AppLayout
      title={t("media.detailTitle")}
      description={id ? `session ${id}` : undefined}
      headerAction={
        id && (
          <div className="flex items-center gap-2">
            <BackLink
              fallback={`/sessions/${id}`}
              className="btn-ghost text-sm"
            >
              ← {t("media.backToSession")}
            </BackLink>
            <DownloadAuditButton sessionId={id} />
            <DownloadReportButton sessionId={id} />
          </div>
        )
      }
    >
      <PermissionGate
        perm="evidence.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            {t("admin.noPermission")}
          </p>
        }
      >
        {id && <MediaBody sessionId={id} />}
      </PermissionGate>
    </AppLayout>
  );
}


function MediaBody({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const [kindFilter, setKindFilter] = useState<EvidenceKind | "">("");
  // Backlog 2026-09-22 — the tab lives in the URL so it can be
  // DEEP-LINKED. The inspection Q&A has always been fillable after a
  // session ends (neither the API nor the panel gates on status), but
  // it was buried behind a click on a tab nobody knew was there. The
  // session list + beneficiary details now link straight to
  // `?tab=qa`, which only works if the tab is addressable.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: "media" | "participants" | "chat" | "qa" =
    tabParam === "participants" ||
    tabParam === "chat" ||
    tabParam === "qa"
      ? tabParam
      : "media";
  const setTab = (next: "media" | "participants" | "chat" | "qa") => {
    const params = new URLSearchParams(searchParams);
    if (next === "media") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  // Open evidence id for the preview modal. EvidenceViewerModal handles
  // raw-vs-annotated overlay for images, native <video> playback for
  // recordings (annotations are already burned into pixels by the
  // post-processor), and the audit metadata panel.
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["session-media", sessionId],
    queryFn: async () => {
      // `final_only=true` hides chain-of-custody raw recordings when
      // an annotated derivative exists. See SessionEvidenceListView
      // — the customer UI shows the polished output; raws stay in
      // storage with legal_hold=True.
      const res = await api.get<Evidence[]>(
        `${ApiPaths.sessionEvidenceList(sessionId)}?with_urls=true&final_only=true`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  // The media page doubles as the finished-session detail view. For a
  // live/pending session we redirect back to the session page so the
  // operator can run the call (see the gate below).
  const session = useQuery({
    queryKey: ["session", sessionId],
    queryFn: async () =>
      (await api.get<Session>(ApiPaths.session(sessionId))).data,
  });

  const evidence = useMemo(() => q.data ?? [], [q.data]);
  const counts = useMemo(() => {
    const c = {
      snapshot: 0,
      whiteboard: 0,
      recording: 0,
      clip: 0,
      document: 0,
      external_audio: 0,
    };
    for (const e of evidence) {
      if (e.kind in c) c[e.kind as keyof typeof c]++;
    }
    return c;
  }, [evidence]);

  const filtered = kindFilter
    ? evidence.filter((e) => e.kind === kindFilter)
    : evidence;

  if (q.isLoading || session.isLoading) {
    return <p className="card text-sm text-nb-text-2">{t("media.loading")}</p>;
  }
  // Bounce a session that is still ahead of its call, with nothing to
  // show, to the session page so the operator can make the call.
  //
  // This used to bounce everything not CLOSED. Reported 2026-10-08: phone
  // audio uploaded after a call that could not connect never appeared —
  // that session is by definition one whose call never ran, so it sits
  // OPEN / EXPIRED / CANCELLED, and the one page that shows its audio
  // redirected away before rendering it. An expired session's own
  // "Media" link looped straight back the same way.
  if (
    session.data &&
    !isTerminal(session.data.status) &&
    evidence.length === 0
  ) {
    return <Navigate to={`/sessions/${sessionId}`} replace />;
  }
  if (q.isError) {
    return (
      <p className="card text-sm text-cvp-danger">
        {(q.error as Error).message}
      </p>
    );
  }

  const mediaCount = counts.recording + counts.clip + counts.snapshot +
    counts.whiteboard + counts.document + counts.external_audio;
  const tabs = [
    { key: "media" as const, label: t("media.tabMedia"), badge: mediaCount },
    { key: "participants" as const, label: t("media.tabParticipants") },
    { key: "chat" as const, label: t("media.tabChat") },
    { key: "qa" as const, label: t("media.tabQa") },
  ];

  return (
    <div className="space-y-4">
      {/* At-a-glance summary — stays visible above the tabs. */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label={t("media.statSnapshots")}
          value={counts.snapshot}
          tone="info"
        />
        <StatCard
          label={t("media.statWhiteboards")}
          value={counts.whiteboard}
          tone="ok"
        />
        <StatCard
          label={t("media.statRecordings")}
          value={counts.recording + counts.clip}
          tone="rose"
        />
        <StatCard
          label={t("media.statDocuments")}
          value={counts.document}
          tone="warn"
        />
        {/* Its own card, not folded into Recordings: a platform recording
            and an operator's uploaded phone audio must never read as the
            same thing. Same label as the card and the Type filter. */}
        <StatCard
          label={t("media.kindExternal_audio")}
          value={counts.external_audio}
          tone="warn"
        />
      </section>

      {/* Tab bar */}
      <div
        role="tablist"
        className="flex flex-wrap gap-1 border-b border-nb-border"
      >
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={tab === tb.key}
            onClick={() => setTab(tb.key)}
            className={
              "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition " +
              (tab === tb.key
                ? "border-nb-mint text-nb-text"
                : "border-transparent text-nb-text-3 hover:text-nb-text-2")
            }
          >
            {tb.label}
            {tb.badge != null && tb.badge > 0 && (
              <span className="rounded-full bg-nb-mint/15 px-1.5 text-[10px] text-nb-mint">
                {tb.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ---- Media tab: filter + evidence gallery ---- */}
      {tab === "media" && (
        <>
          <section className="card">
            <label className="flex items-baseline gap-3 text-sm">
              <span className="text-nb-text-2">{t("media.filterKind")}:</span>
              <select
                className="input"
                value={kindFilter}
                onChange={(e) =>
                  setKindFilter(e.target.value as EvidenceKind | "")
                }
              >
                <option value="">{t("media.filterKindAll")}</option>
                <option value="snapshot">{t("media.kindSnapshot")}</option>
                <option value="whiteboard">{t("media.kindWhiteboard")}</option>
                <option value="recording">{t("media.kindRecording")}</option>
                <option value="clip">{t("media.kindClip")}</option>
                <option value="document">{t("media.kindDocument")}</option>
                <option value="external_audio">
                  {t("media.kindExternal_audio")}
                </option>
              </select>
              <span className="text-xs text-nb-text-3">
                {filtered.length} / {evidence.length}
              </span>
            </label>
          </section>

          {evidence.length === 0 ? (
            <p className="card text-sm text-nb-text-2">{t("media.empty")}</p>
          ) : (
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((ev) => (
                <EvidenceCard
                  key={ev.id}
                  ev={ev}
                  t={t}
                  onOpen={() => setOpenId(ev.id)}
                  onOpenId={setOpenId}
                />
              ))}
            </section>
          )}
        </>
      )}

      {/* ---- Participants tab ---- */}
      {tab === "participants" && <ParticipantsCard sessionId={sessionId} />}

      {/* ---- Chat transcript tab (read-only) ---- */}
      {tab === "chat" && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("media.chatTranscript")}
          </h2>
          <div className="h-[32rem] overflow-hidden rounded-md border border-nb-border">
            <ChatPanel sessionId={sessionId} readOnly />
          </div>
        </section>
      )}

      {/* ---- Inspection Q&A tab (panel self-hides when no questions) ---- */}
      {tab === "qa" && <InspectionQaPanel sessionId={sessionId} />}

      {openId && (
        <EvidenceViewerModal
          evidenceId={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}


type ParticipantDetail = {
  id: string;
  role: string;
  display_name: string;
  joined_at: string | null;
  left_at: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: {
    legal_name: string;
    registration_number: string;
    vat_id: string;
    address_line: string;
    city: string;
    postal_code: string;
    country: string;
  };
  source: string;
};


function participantName(p: ParticipantDetail): string {
  const full = `${p.first_name} ${p.last_name}`.trim();
  return full || p.display_name || p.id.slice(0, 8);
}


/** Read-only participant roster for the finished-session detail — shows a
 *  name + role per row; click a row to open the full-details popup. */
function ParticipantsCard({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ParticipantDetail | null>(null);
  const q = useQuery({
    queryKey: ["session-participant-details", sessionId],
    queryFn: async () => {
      const res = await api.get<ParticipantDetail[]>(
        `/v1/sessions/${sessionId}/participant-details`,
      );
      return res.data;
    },
  });
  const rows = q.data ?? [];
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("media.participants", { count: rows.length })}
      </h2>
      {rows.length === 0 ? (
        <p className="text-xs text-nb-text-3">{t("media.noParticipants")}</p>
      ) : (
        <ul className="divide-y divide-nb-border/60 text-sm">
          {rows.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelected(p)}
                className="flex w-full items-center gap-2 py-1.5 text-left hover:bg-nb-surface/40 rounded px-1"
              >
                <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] uppercase text-nb-mint">
                  {p.role}
                </span>
                <span className="min-w-0 flex-1 truncate text-nb-text">
                  {participantName(p)}
                  {p.company.legal_name && (
                    <span className="ml-2 text-[11px] text-nb-text-3">
                      · {p.company.legal_name}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-nb-mint">
                  {t("media.ptDetails")} →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <ParticipantDetailModal
          p={selected}
          t={t}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}


function ParticipantDetailModal({
  p,
  t,
  onClose,
}: {
  p: ParticipantDetail;
  t: ReturnType<typeof useTranslation>["t"];
  onClose: () => void;
}) {
  const rowsData: [string, string][] = [
    [t("media.ptName"), `${p.first_name} ${p.last_name}`.trim() || "—"],
    [t("media.ptRole"), p.role],
    [t("contacts.col.email", "Email"), p.email || "—"],
    [t("contacts.col.phone", "Phone"), p.phone || "—"],
    [t("media.ptCompany"), p.company.legal_name || "—"],
    [t("media.ptRegistration"), p.company.registration_number || "—"],
    [t("media.ptVat"), p.company.vat_id || "—"],
    [
      t("media.ptAddress"),
      [
        p.company.address_line,
        [p.company.postal_code, p.company.city].filter(Boolean).join(" "),
        p.company.country,
      ]
        .filter(Boolean)
        .join(", ") || "—",
    ],
    [t("media.ptJoinedAt"), p.joined_at ? formatDateTime(p.joined_at) : "—"],
    [t("media.ptLeftAt"), p.left_at ? formatDateTime(p.left_at) : "—"],
  ];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("media.ptDetailsTitle")}
      className="modal-backdrop bg-black/70"
      onClick={onClose}
    >
      <div
        className="modal-panel w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">{participantName(p)}</h2>
          <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] uppercase text-nb-mint">
            {p.role}
          </span>
        </header>
        <dl className="space-y-2 text-sm">
          {rowsData.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <dt className="text-nb-text-3">{label}</dt>
              <dd className="text-right text-nb-text">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex justify-end">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}


function EvidenceCard({
  ev,
  t,
  onOpen,
  onOpenId,
}: {
  ev: Evidence;
  t: ReturnType<typeof useTranslation>["t"];
  onOpen: () => void;
  /** Open an arbitrary evidence id in the viewer (used for "View signed
   *  PDF", which opens the signed copy rather than this row). */
  onOpenId: (id: string) => void;
}) {
  const sig = ev.signature ?? null;
  const isSignedCopy = sig?.role === "signed_copy";
  const isSignedSource =
    sig?.role === "source" && sig.status === "completed";
  const isSignPending =
    sig?.role === "source" && sig.status !== "completed";
  const kindLabel = t(`media.kind${capitalize(ev.kind)}`, {
    defaultValue: t("media.kindUnknown"),
  });
  const isImage = ev.kind === "snapshot" || ev.kind === "whiteboard";
  const isVideo = ev.kind === "clip" || ev.kind === "recording";
  const isDoc = ev.kind === "document";
  // Uploaded phone-interview audio. Its own player: a <video> element
  // shows a black rectangle for an audio-only file, which reads as a
  // broken recording.
  const isAudio = ev.kind === "external_audio";
  const url = ev.download_url ?? null;
  const geo = (ev.geo_jsonb || {}) as {
    lat?: number;
    lng?: number;
    lon?: number;
    accuracy_m?: number;
    accuracy?: number;
  };
  const lat = geo.lat;
  const lon = geo.lng ?? geo.lon;
  const accuracy = geo.accuracy_m ?? geo.accuracy;

  return (
    <article className="card flex flex-col overflow-hidden">
      {/* Preview — click anywhere on the thumbnail to open the modal
          (image with annotation overlay toggle, or video playback).
          The download anchor in the footer stays a separate action. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={t("media.openViewer", { defaultValue: "Open viewer" })}
        className="-mx-5 -mt-5 mb-4 block aspect-video overflow-hidden border-b border-nb-border bg-slate-900 text-left"
      >
        {isImage && url && (
          <img
            src={url}
            alt={kindLabel}
            loading="lazy"
            className="h-full w-full object-cover transition-transform hover:scale-105"
          />
        )}
        {isImage && !url && (
          <div className="grid h-full place-items-center text-xs text-nb-text-3">
            {ev.status === "pending"
              ? t("media.pendingBadge")
              : t("media.failedBadge")}
          </div>
        )}
        {/* Video preview in the grid stays as a native <video> with
            controls so the operator can scrub without opening the
            modal; click on the chrome area outside the controls
            still falls through to the button. Annotated derivatives
            have annotations burned into pixels so the player is
            authoritative for recordings. */}
        {isVideo && url && (
          <video
            controls
            preload="metadata"
            src={url}
            className="h-full w-full object-contain bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <track kind="captions" />
          </video>
        )}
        {isVideo && !url && (
          <div className="grid h-full place-items-center text-xs text-nb-text-3">
            {ev.status === "pending"
              ? t("media.pendingBadge")
              : t("media.failedBadge")}
          </div>
        )}
        {isAudio && (
          <div
            className="flex h-full flex-col justify-center gap-2 bg-nb-surface/60 px-3"
            onClick={(e) => e.stopPropagation()}
          >
            {url ? (
              <audio controls preload="metadata" src={url} className="w-full" />
            ) : (
              <p className="text-center text-xs text-nb-text-3">
                {ev.status === "pending"
                  ? t("media.pendingBadge")
                  : t("media.failedBadge")}
              </p>
            )}
            {/* Says where it came from wherever it is shown. A reader
                must never have to assume this was captured by the
                platform. */}
            <p className="text-center text-[10px] leading-tight text-amber-400">
              {t("evidence.externalAudioProvenance")}
            </p>
          </div>
        )}
        {isDoc && (
          <div className="grid h-full place-items-center bg-nb-surface/60">
            <DocumentIcon />
          </div>
        )}
      </button>

      {/* Metadata */}
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-nb-text">{kindLabel}</h3>
        <span className="text-xs text-nb-text-3">
          {formatDateTime(ev.captured_at)}
        </span>
      </header>

      <dl className="mt-3 space-y-1.5 text-xs">
        {lat != null && lon != null && (
          <div className="flex justify-between gap-2">
            <dt className="text-nb-text-3">{t("media.gpsLabel")}</dt>
            <dd className="font-mono text-nb-text">
              {lat.toFixed(5)}, {lon.toFixed(5)}
              {accuracy != null && (
                <> · {t("media.gpsAccuracy", { accuracy: Math.round(accuracy) })}</>
              )}
            </dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="text-nb-text-3">{t("media.sizeLabel")}</dt>
          <dd className="font-mono text-nb-text">
            {formatBytes(ev.byte_size)} · {ev.mime}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-nb-text-3">{t("media.shaLabel")}</dt>
          <dd className="truncate font-mono text-nb-text-2" title={ev.sha256}>
            {ev.sha256.slice(0, 12)}…{ev.sha256.slice(-4)}
          </dd>
        </div>
      </dl>

      <footer className="mt-4 flex items-center justify-between gap-2 border-t border-nb-border pt-3">
        <div className="flex items-center gap-1.5">
          {ev.status !== "ready" && (
            <span
              className={
                "rounded px-1.5 py-0.5 text-[10px] font-medium " +
                (ev.status === "pending"
                  ? "bg-amber-500/15 text-amber-300"
                  : "bg-rose-500/15 text-rose-300")
              }
            >
              {ev.status === "pending"
                ? t("media.pendingBadge")
                : ev.status === "tombstoned"
                  ? t("media.tombstonedBadge")
                  : t("media.failedBadge")}
            </span>
          )}
          {ev.legal_hold && (
            <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300">
              hold
            </span>
          )}
          {(isSignedCopy || isSignedSource) && (
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
              ✓{" "}
              {isSignedCopy
                ? t("media.signedCopyBadge")
                : t("media.eSignedBadge")}
            </span>
          )}
          {isSignPending && (
            <span className="rounded bg-nb-mute/20 px-1.5 py-0.5 text-[10px] font-medium text-nb-text-2">
              {t("media.signPendingBadge")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isSignedSource && sig?.signed_pdf_evidence_id && (
            <button
              type="button"
              onClick={() => onOpenId(sig.signed_pdf_evidence_id as string)}
              className="text-xs font-medium text-emerald-300 hover:text-emerald-200"
            >
              {t("media.viewSignedPdf")}
            </button>
          )}
          {url && (
            <button
              type="button"
              onClick={() => {
                void downloadEvidenceBlob(url, suggestEvidenceFilename(ev));
              }}
              className="text-xs font-medium text-nb-mint hover:text-blue-400"
            >
              {t("media.download")} ↓
            </button>
          )}
        </div>
      </footer>
    </article>
  );
}


/** Cross-origin download helper. The presigned MinIO URL is a
 *  different origin from the SPA (`app.nexbasira.com` vs
 *  `minio.codelounge.io`); the browser ignores the `download` attribute
 *  on cross-origin anchors per WHATWG #downloading-resources. Fetch
 *  the bytes ourselves into a same-origin blob and then trigger an
 *  anchor click — works under popup blockers AND honours the suggested
 *  filename. */
async function downloadEvidenceBlob(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) return;
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}


/** Build a human-friendly filename from the Evidence row. Keeps the
 *  source extension when one is present in `storage_key`, defaults to
 *  the mime extension otherwise. */
function suggestEvidenceFilename(ev: Evidence): string {
  const fromKey = ev.storage_key.split("/").pop() ?? "";
  if (fromKey && fromKey.includes(".")) return fromKey;
  const extByMime: Record<string, string> = {
    "video/mp4": "mp4",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "application/pdf": "pdf",
  };
  const ext = extByMime[ev.mime] ?? "bin";
  return `${ev.kind}-${ev.id.slice(0, 8)}.${ext}`;
}


/** Opens the language picker + report library (per-language, eIDAS-timestamped,
 * one-time-generated reports) in a modal — replaces the old direct download of
 * the unsigned French-only PDF. */
function DownloadReportButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn-primary text-sm"
        onClick={() => setOpen(true)}
        data-testid="media-report-open"
      >
        {t("media.downloadReport")}
      </button>
      {open && (
        <div
          className="modal-backdrop bg-black/60"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md space-y-3 rounded-lg border border-nb-border bg-nb-bg p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                {t("media.downloadReport")}
              </h2>
              <button
                type="button"
                className="text-lg leading-none text-nb-text-2"
                onClick={() => setOpen(false)}
                aria-label={t("common.close")}
              >
                ✕
              </button>
            </div>
            <InspectionReportPanel sessionId={sessionId} />
          </div>
        </div>
      )}
    </>
  );
}


/** Download the tamper-evident audit-trail report (chain of custody). */
function DownloadAuditButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const mut = useMutation({
    mutationFn: async () => {
      const res = await api.get(ApiPaths.sessionAuditReportPdf(sessionId), {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-trail-${sessionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
  return (
    <button
      type="button"
      className="btn-ghost text-sm"
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
    >
      {mut.isPending ? t("media.downloadingAudit") : t("media.downloadAudit")}
    </button>
  );
}


function DocumentIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 3h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.4"
        className="text-nb-text-2"
      />
      <path
        d="M15 3v5h5"
        stroke="currentColor"
        strokeWidth="1.4"
        className="text-nb-text-2"
      />
    </svg>
  );
}


/** The session is over — whatever it holds is final. Mirrors the
 *  drawer's private copy. */
function isTerminal(status: Session["status"]): boolean {
  return ["closed", "expired", "cancelled"].includes(status);
}


function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
